/**
 * Adventure Mode Constants
 * アドベンチャーモードの定数定義
 *
 * NPCの順序はシミュレーション勝率に基づいて設定
 * 参照: src/ルール/CardValueSystem/adventure_mode.md
 *
 * Created: 2025-12-17
 */

import type {
  NpcId,
  ChapterConfig,
  AdventureNpcConfig,
  DifficultyTier,
  DifficultyStars,
} from './types';

// ============================================================
// 章の定義
// ============================================================

export const CHAPTERS: ChapterConfig[] = [
  {
    id: 1,
    name: 'はじまりの試練',
    description: '入門編：基本を学ぼう',
    npcs: ['elda', 'sisterEmma', 'seira'],
    unlockRequirement: null,
  },
  {
    id: 2,
    name: 'コレクターの挑戦',
    description: '初級：カードコレクターたちとの戦い',
    npcs: ['misty', 'pippo'],
    unlockRequirement: 'seira',
  },
  {
    id: 3,
    name: '四天王の試練',
    description: '中級：実力者たちとの戦い',
    npcs: ['rowen', 'garon', 'toma'],
    unlockRequirement: 'pippo',
  },
  {
    id: 4,
    name: '闇の脅威',
    description: '上級：闘の使い手たちとの戦い',
    npcs: ['malik', 'liliane', 'nova'],
    unlockRequirement: 'toma',
  },
  {
    id: 5,
    name: '最強への道',
    description: '最上級：最強の対戦相手たち',
    npcs: ['profNome', 'galdos', 'kain'],
    unlockRequirement: 'nova',
  },
  {
    id: 6,
    name: '運命の対決',
    description: '最終章：全ての決着',
    npcs: ['mask', 'doppelganger'],
    unlockRequirement: 'kain',
  },
];

// ============================================================
// NPC設定（難易度順）
// ============================================================

/**
 * アドベンチャー用NPC設定
 * シミュレーション勝率に基づく難易度順
 */
export const ADVENTURE_NPC_CONFIG: AdventureNpcConfig[] = [
  // 第1章：はじまりの試練（入門）- D Tier
  {
    npcId: 'elda',
    chapter: 1,
    order: 1,
    deckName: '聖なる巨鳥',
    winRate: 37.3,
    tier: 'D',
    difficultyStars: 1,
    unlockRequirements: [],
    firstClearReward: {
      gold: 300,
      packs: [{ type: 'normal', count: 1 }],
    },
    repeatReward: {
      goldMin: 100,
      goldMax: 200,
      packChance: 0.1,
      packType: 'normal',
    },
  },
  {
    npcId: 'sisterEmma',
    chapter: 1,
    order: 2,
    deckName: '聖なる巨鳥',
    winRate: 37.3,
    tier: 'D',
    difficultyStars: 1,
    unlockRequirements: ['elda'],
    firstClearReward: {
      gold: 400,
      packs: [{ type: 'light', count: 1 }],
    },
    repeatReward: {
      goldMin: 100,
      goldMax: 200,
      packChance: 0.1,
      packType: 'light',
    },
  },
  {
    npcId: 'seira',
    chapter: 1,
    order: 3,
    deckName: '深海の支配者',
    winRate: 38.7,
    tier: 'D',
    difficultyStars: 1,
    unlockRequirements: ['sisterEmma'],
    firstClearReward: {
      gold: 500,
      packs: [{ type: 'water', count: 1 }],
    },
    repeatReward: {
      goldMin: 100,
      goldMax: 200,
      packChance: 0.1,
      packType: 'water',
    },
  },

  // 第2章：コレクターの挑戦（初級）- C/B Tier
  {
    npcId: 'misty',
    chapter: 2,
    order: 1,
    deckName: '氷眠の檻',
    winRate: 48.0,
    tier: 'C',
    difficultyStars: 2,
    unlockRequirements: ['seira'],
    firstClearReward: {
      gold: 800,
      packs: [{ type: 'water', count: 2 }],
    },
    repeatReward: {
      goldMin: 150,
      goldMax: 300,
      packChance: 0.15,
      packType: 'water',
    },
  },
  {
    npcId: 'pippo',
    chapter: 2,
    order: 2,
    deckName: '熔岩の王国',
    winRate: 53.0,
    tier: 'B',
    difficultyStars: 3,
    unlockRequirements: ['misty'],
    firstClearReward: {
      gold: 1000,
      packs: [{ type: 'fire', count: 1 }],
    },
    repeatReward: {
      goldMin: 200,
      goldMax: 400,
      packChance: 0.15,
      packType: 'fire',
    },
  },

  // 第3章：四天王の試練（中級）- B Tier
  {
    npcId: 'rowen',
    chapter: 3,
    order: 1,
    deckName: '聖域の光',
    winRate: 51.2,
    tier: 'B',
    difficultyStars: 3,
    unlockRequirements: ['pippo'],
    firstClearReward: {
      gold: 1000,
      packs: [{ type: 'light', count: 2 }],
    },
    repeatReward: {
      goldMin: 200,
      goldMax: 400,
      packChance: 0.15,
      packType: 'light',
    },
  },
  {
    npcId: 'garon',
    chapter: 3,
    order: 2,
    deckName: '呪森の狩人',
    winRate: 57.1,
    tier: 'B',
    difficultyStars: 3,
    unlockRequirements: ['pippo'],
    firstClearReward: {
      gold: 1200,
      packs: [{ type: 'primitive', count: 2 }],
    },
    repeatReward: {
      goldMin: 200,
      goldMax: 400,
      packChance: 0.15,
      packType: 'primitive',
    },
  },
  {
    npcId: 'toma',
    chapter: 3,
    order: 3,
    deckName: 'プニリーヌの騎士団',
    winRate: 59.2,
    tier: 'B',
    difficultyStars: 3,
    unlockRequirements: ['rowen', 'garon'],
    firstClearReward: {
      gold: 1500,
      packs: [{ type: 'normal', count: 3 }],
    },
    repeatReward: {
      goldMin: 200,
      goldMax: 400,
      packChance: 0.15,
      packType: 'normal',
    },
  },

  // 第4章：闇の脅威（上級）- A Tier
  {
    npcId: 'malik',
    chapter: 4,
    order: 1,
    deckName: '闇鎖の舞踏',
    winRate: 63.8,
    tier: 'A',
    difficultyStars: 4,
    unlockRequirements: ['toma'],
    firstClearReward: {
      gold: 2000,
      packs: [{ type: 'dark', count: 2 }],
    },
    repeatReward: {
      goldMin: 400,
      goldMax: 600,
      packChance: 0.2,
      packType: 'dark',
    },
  },
  {
    npcId: 'liliane',
    chapter: 4,
    order: 2,
    deckName: '幼魔王女の支配',
    winRate: 65.4,
    tier: 'A',
    difficultyStars: 4,
    unlockRequirements: ['malik'],
    firstClearReward: {
      gold: 2000,
      packs: [{ type: 'dark', count: 2 }],
    },
    repeatReward: {
      goldMin: 400,
      goldMax: 600,
      packChance: 0.2,
      packType: 'dark',
    },
  },
  {
    npcId: 'nova',
    chapter: 4,
    order: 3,
    deckName: '紫鴉の預言者',
    winRate: 67.0,
    tier: 'A',
    difficultyStars: 4,
    unlockRequirements: ['liliane'],
    firstClearReward: {
      gold: 2500,
      packs: [{ type: 'future', count: 3 }],
    },
    repeatReward: {
      goldMin: 400,
      goldMax: 600,
      packChance: 0.2,
      packType: 'future',
    },
  },

  // 第5章：最強への道（最上級）- S Tier
  {
    npcId: 'profNome',
    chapter: 5,
    order: 1,
    deckName: '魔導遺跡の守護者',
    winRate: 70.1,
    tier: 'S',
    difficultyStars: 5,
    unlockRequirements: ['nova'],
    firstClearReward: {
      gold: 3000,
      packs: [{ type: 'future', count: 3 }],
      title: '分析の克服者',
    },
    repeatReward: {
      goldMin: 600,
      goldMax: 1000,
      packChance: 0.25,
      packType: 'future',
    },
  },
  {
    npcId: 'galdos',
    chapter: 5,
    order: 2,
    deckName: '紅蓮龍炎',
    winRate: 72.7,
    tier: 'S',
    difficultyStars: 5,
    unlockRequirements: ['profNome'],
    firstClearReward: {
      gold: 3500,
      packs: [
        { type: 'fire', count: 2 },
        { type: 'light', count: 1 },
      ],
    },
    repeatReward: {
      goldMin: 600,
      goldMax: 1000,
      packChance: 0.25,
      packType: 'fire',
    },
  },
  {
    npcId: 'kain',
    chapter: 5,
    order: 3,
    deckName: '紅蓮の咆哮',
    winRate: 73.5,
    tier: 'S',
    difficultyStars: 5,
    unlockRequirements: ['galdos'],
    firstClearReward: {
      gold: 5000,
      packs: [{ type: 'fire', count: 3 }],
      title: '紅蓮を超えし者',
    },
    repeatReward: {
      goldMin: 600,
      goldMax: 1000,
      packChance: 0.25,
      packType: 'fire',
    },
  },

  // 第6章：運命の対決（最終章）
  {
    npcId: 'mask',
    chapter: 6,
    order: 1,
    deckName: '闇の預言者',
    winRate: 50.1,
    tier: 'B',
    difficultyStars: 3,
    unlockRequirements: ['kain'],
    firstClearReward: {
      gold: 5000,
      packs: [{ type: 'royal', count: 1 }],
      title: '闇を見た者',
    },
    repeatReward: {
      goldMin: 600,
      goldMax: 1000,
      packChance: 0.25,
      packType: 'royal',
    },
  },
  {
    npcId: 'doppelganger',
    chapter: 6,
    order: 2,
    deckName: 'プレイヤーコピー',
    winRate: 0, // 不定
    tier: 'S',
    difficultyStars: 5,
    unlockRequirements: ['mask'],
    firstClearReward: {
      gold: 10000,
      packs: [{ type: 'royal', count: 2 }],
      title: '己に勝つ者',
    },
    repeatReward: {
      goldMin: 800,
      goldMax: 1500,
      packChance: 0.3,
      packType: 'royal',
    },
  },
];

// ============================================================
// 全クリアボーナス
// ============================================================

export const ALL_CLEAR_BONUS = {
  gold: 20000,
  packs: [{ type: 'royal' as const, count: 3 }],
  title: 'マジックスピリット・マスター',
};

// ============================================================
// デイリー制限
// ============================================================

export const DAILY_BATTLE_LIMIT = 3; // 同一NPCとの1日の報酬獲得可能回数

// ============================================================
// ヘルパー関数
// ============================================================

/**
 * NPC IDから設定を取得
 */
export function getNpcConfig(npcId: NpcId): AdventureNpcConfig | undefined {
  return ADVENTURE_NPC_CONFIG.find((config) => config.npcId === npcId);
}

/**
 * 章IDから設定を取得
 */
export function getChapterConfig(chapterId: number): ChapterConfig | undefined {
  return CHAPTERS.find((chapter) => chapter.id === chapterId);
}

/**
 * 章に所属するNPC設定を取得
 */
export function getChapterNpcs(chapterId: number): AdventureNpcConfig[] {
  return ADVENTURE_NPC_CONFIG.filter((config) => config.chapter === chapterId);
}

/**
 * 難易度ティアから星数を取得
 */
export function tierToStars(tier: DifficultyTier): DifficultyStars {
  const mapping: Record<DifficultyTier, DifficultyStars> = {
    D: 1,
    C: 2,
    B: 3,
    A: 4,
    S: 5,
  };
  return mapping[tier];
}

/**
 * 星数を文字列で表示
 */
export function starsToString(stars: DifficultyStars): string {
  const filled = '★'.repeat(stars);
  const empty = '☆'.repeat(5 - stars);
  return filled + empty;
}

/**
 * パックタイプの日本語名
 */
export function getPackTypeName(
  type: 'normal' | 'fire' | 'water' | 'light' | 'dark' | 'primitive' | 'future' | 'royal'
): string {
  const names: Record<string, string> = {
    normal: 'ノーマルパック',
    fire: '炎パック',
    water: '水パック',
    light: '光パック',
    dark: '闇パック',
    primitive: '原始パック',
    future: '未来パック',
    royal: 'ロイヤルパック',
  };
  return names[type] || type;
}
